import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import { Box, Grid } from "@mui/material";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useEffect, useRef } from "react";
import CustomDatePicker from "../../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../../components/CustomSelect";
import CustomSelectScroll from "@/components/CustomSelectScroll";
import Input from "../../../../../../components/Input";
import {
  STATE_LIST,
  STATE_RECORD_FULL_NAME_MAPPER,
} from "../../../../../../constants";
// import { useProviderListV2 } from "../../../../../../queries/use-provider-list-v2";
import CardView from "../../../patient/patient-list/patient-chart-new/assessment/summary/components/CardView";
import { placesOfService } from "../../add-super-bill-components/places-of-service";
import {
  isTelehealthPlaceOfService,
  useServiceLocationBilling,
} from "../../use-service-location-billing";
import { useServiceLocationOptions } from "../../use-service-location-options";
import { UPDATE_CLAIM_FORM_TYPE } from "../claims-constants";
// import { useProviderNpiAutofill } from "./use-provider-npi-autofill";
interface ServiceDetailsClaimProps {
  formik: FormikProps<UPDATE_CLAIM_FORM_TYPE>;
  readOnly?: boolean;
}
const ServiceDetailsClaim = ({
  formik,
  readOnly,
}: ServiceDetailsClaimProps) => {
  // const [searchProviderKey, setSearchProviderKey] = useState<string>("");
  const { values, setFieldValue, errors, touched } = formik;

  // const renderingProviderUuid = Object.keys(
  //   values.renderingproviderId ?? {}
  // )[0];
  // const { markSelected: markRenderingProviderSelected } =
  //   useProviderNpiAutofill({
  //     providerUuid: renderingProviderUuid,
  //     tenantId: xTENANTID,
  //     currentValue: values.renderingProviderNPINumber,
  //     setValue: (npi) => setFieldValue("renderingProviderNPINumber", npi),
  //   });

  // Keeps the saved Service Location selectable even when it falls outside the
  // page the Locations search currently returns.
  const {
    options: serviceLocationOptions,
    onSearch: onServiceLocationSearch,
    toServiceLocation,
    pagination: serviceLocationPagination,
    setPagination: setServiceLocationPagination,
    totalElements: serviceLocationTotalElements,
    loading: serviceLocationLoading,
  } = useServiceLocationOptions(values.locationId);

  // ECRH-5370: Service Location is optional for Telehealth encounters.
  const isTelehealth = isTelehealthPlaceOfService(values.placeOfService);
  const isEditable = !readOnly;
  const selectedLocationUuid = Object.keys(values.locationId ?? {})?.[0];

  // Resolve the Billing Address (and Service State) from the selected Service
  // Location, or from the Provider Group Profile when none is selected.
  const {
    serviceState: resolvedServiceState,
    serviceLocationAddress: resolvedBillingAddress,
    isResolved: isBillingResolved,
  } = useServiceLocationBilling(selectedLocationUuid);

  // Track the previously-resolved location so we can tell a real Service
  // Location change/removal apart from a form reinitialize (prefill) or a manual
  // Service State edit.
  const prevLocationRef = useRef<string | undefined>(undefined);

  // Keep Service State + the Billing Address in sync with the resolved source,
  // only while the form is editable:
  //  - When a Service Location is selected, it always drives the Service State
  //    from that Location's Billing Address.
  //  - When no Service Location is selected, default the Service State from the
  //    Provider Group (Billing Provider) state whenever the location was just
  //    cleared, or on initial load / prefill while it is still empty — so it
  //    auto-populates without any user interaction and survives the form's
  //    reinitialize. A manual dropdown selection is otherwise preserved.
  // An already-populated Service State is never blanked out: neither source can
  // clear the field, only replace it with a state it actually resolved.
  useEffect(() => {
    if (!isEditable || !isBillingResolved) return;

    const locationChanged = prevLocationRef.current !== selectedLocationUuid;
    prevLocationRef.current = selectedLocationUuid;

    const stateDiffers =
      (values.serviceState ?? "") !== (resolvedServiceState ?? "");

    if (selectedLocationUuid) {
      // Location selected -> it drives the Service State, but only when that
      // Location actually provides one. If the Location has no billing state
      // configured, never wipe an existing/prefilled value or a manual
      // selection with an empty string -> let the user pick one manually.
      if (resolvedServiceState && stateDiffers)
        setFieldValue("serviceState", resolvedServiceState);
    } else if (
      resolvedServiceState &&
      (locationChanged || !values.serviceState) &&
      stateDiffers
    ) {
      // No location -> default from the Provider Group state. Guarded on the
      // Provider Group actually having one: removing the Service Location must
      // not wipe a populated Service State when there is nothing to replace it
      // with, which then fails the required-field check on save.
      setFieldValue("serviceState", resolvedServiceState);
    }
    // Only overwrite the Billing Address when the resolved source actually has
    // one, or when the user changed the Service Location. Otherwise a Location
    // with no Billing Address configured would wipe the address the backend
    // already resolved onto the claim.
    if (resolvedBillingAddress || locationChanged)
      setFieldValue("serviceLocationAddress", resolvedBillingAddress ?? null);
  }, [
    isEditable,
    isBillingResolved,
    resolvedServiceState,
    resolvedBillingAddress,
    selectedLocationUuid,
    values.serviceState,
  ]);

  // const { data: providersList } = useProviderListV2({
  //   status: true,
  //   pagination: DEF_PAGINATION,
  //   searchString: searchProviderKey,
  //   xTENANTID: localStorage.getItem("realm") ?? "",
  // });

  return (
    <Box id={"Provider Information"}>
      <CardView
        title="Service Details"
        titleIcon={
          <MedicalServicesOutlinedIcon
            sx={{ color: "primary.main" }}
            fontSize="small"
          />
        }
      >
        {/* <CustomTitle mb={1}>Service Details</CustomTitle> */}
        <Grid container spacing={2} direction={"column"} p={0.8}>
          <Grid item container spacing={2}>
            <Grid item xs={3}>
              <CustomSelectScroll
                required={!isTelehealth}
                name="locationId"
                label="Service Location"
                readOnly={readOnly}
                placeholder="Select Service Location"
                options={serviceLocationOptions}
                onSearch={(_, val) => onServiceLocationSearch(val)}
                value={selectedLocationUuid ?? undefined}
                onChange={(value) =>
                  setFieldValue("locationId", toServiceLocation(value))
                }
                pagination={serviceLocationPagination}
                setPagination={setServiceLocationPagination}
                totalElements={serviceLocationTotalElements}
                loading={serviceLocationLoading}
                error={touched?.locationId && errors.locationId}
                handleBlur={formik.handleBlur}
              />
            </Grid>
            <Grid item xs={3}>
              <CustomSelect
                readOnly={readOnly}
                required
                name="placeOfService"
                label="Place Of Service"
                options={placesOfService}
                value={values.placeOfService}
                onChange={(value) => {
                  setFieldValue("placeOfService", value);
                }}
                error={touched?.placeOfService && errors.placeOfService}
                handleBlur={formik.handleBlur}
              />
            </Grid>
            <Grid item xs={3}>
              <CustomDatePicker
                required
                name="dateOfService"
                disabled={readOnly}
                maxDate={dayjs(new Date())}
                label="Date Of Service"
                value={
                  values.dateOfService ? dayjs(values.dateOfService) : null
                }
                onChange={(value) => setFieldValue("dateOfService", value)}
                placeholder="Date Of Service"
                error={touched?.dateOfService && errors.dateOfService}
                handleBlur={formik.handleBlur}
              />
            </Grid>
            <Grid item xs={3}>
              {readOnly ? (
                <Input
                  label="Service State"
                  textFieldProps={{
                    disabled: readOnly,
                    placeholder: "Service State",
                    value: STATE_RECORD_FULL_NAME_MAPPER[values?.serviceState],
                  }}
                />
              ) : (
                <CustomSelect
                  required
                  readOnly={readOnly}
                  label="Service State"
                  name="serviceState"
                  options={STATE_LIST}
                  value={values.serviceState}
                  onChange={(option) => {
                    formik.setFieldValue("serviceState", option);
                  }}
                  error={touched?.serviceState && errors.serviceState}
                  handleBlur={formik.handleBlur}
                />
              )}
            </Grid>
            {/* <Grid item xs={3}>
              <CustomSelect
                readOnly={readOnly}
                required
                name="renderingproviderId"
                label="Rendering Provider"
                placeholder="Select Rendering Provider"
                options={providersList?.data?.content?.map((it) => ({
                  value: it?.uuid,
                  label: it?.name,
                }))}
                onSearch={(_, val) => setSearchProviderKey(val)}
                value={
                  Object.keys(values.renderingproviderId ?? {})?.[0] ??
                  undefined
                }
                onChange={(value: string | null) => {
                  const curOption = providersList?.data?.content?.find(
                    (it) => it.uuid === value
                  );
                  setFieldValue(
                    "renderingproviderId",
                    curOption
                      ? {
                          [curOption.uuid]: curOption?.name,
                        }
                      : undefined
                  );
                  markRenderingProviderSelected(value);
                  if (!value) setFieldValue("renderingProviderNPINumber", "");
                }}
                error={
                  touched?.renderingproviderId && errors.renderingproviderId
                }
                handleBlur={formik.handleBlur}
              />
            </Grid> */}
            {/* <Grid item xs={3}>
              <Input
                required
                label="Rendering Provider NPI"
                error={
                  touched?.renderingProviderNPINumber &&
                  errors.renderingProviderNPINumber
                }
                textFieldProps={{
                  name: "renderingProviderNPINumber",
                  disabled: readOnly,
                  placeholder: "Rendering Provider NPI",
                  value: values?.renderingProviderNPINumber ?? "",
                  onBlur: formik.handleBlur,
                  onChange: (e) =>
                    setFieldValue(
                      "renderingProviderNPINumber",
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    ),
                  inputProps: { maxLength: 10, inputMode: "numeric" },
                }}
              />
            </Grid> */}
            <Grid item xs={3}>
              <Input
                label="Prior Authorization"
                textFieldProps={{
                  disabled: readOnly,
                  placeholder: "Prior Authorization",
                  value: values?.priorAuthorization,
                  onChange: (e) =>
                    formik.setFieldValue("priorAuthorization", e.target.value),
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </CardView>
    </Box>
  );
};

export default ServiceDetailsClaim;
